<template>
  <div class="contents">
    <div class="layout">
      <div class="breadcrumb">
        <div class="breadcrumb-item">기업 관리</div>
        <div class="breadcrumb-item active">기업 계정 관리</div>
      </div>

      <div class="page-header">
        <h1 class="page-title">기업 계정 관리</h1>
        <div class="page-actions">
          <button
            v-permission="'ADMIN_WRITE'"
            @click="openCreateModal"
            class="btn btn-primary"
          >
            <i class="icon-plus"></i>
            계정 추가
          </button>
        </div>
      </div>

      <div class="white-panel mt20">
        <div class="table-wrap">
          <div class="scroll-box">
            <table class="table-type1">
              <colgroup>
                <col style="width: 5%" />
                <!-- NO -->
                <col style="width: 15%" />
                <!-- 계정ID -->
                <col style="width: 15%" />
                <!-- 이름 -->
                <col style="width: 20%" />
                <!-- 기업명 -->
                <col style="width: 10%" />
                <!-- 역할 -->
                <col style="width: 15%" />
                <!-- 이메일 -->
                <col style="width: 10%" />
                <!-- 생성일 -->
                <col style="width: 10%" />
                <!-- 액션 -->
              </colgroup>
              <thead>
                <tr>
                  <th>NO</th>
                  <th>계정 ID</th>
                  <th>이름</th>
                  <th>기업명</th>
                  <th>역할</th>
                  <th>이메일</th>
                  <th>생성일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(user, index) in businessUsers" :key="user.id">
                  <td>{{ index + 1 }}</td>
                  <td class="font-weight-bold">{{ user.accountId }}</td>
                  <td>{{ user.name || '-' }}</td>
                  <td>
                    <div
                      v-if="
                        user.organizationName ||
                        (user.organization && user.organization.name)
                      "
                    >
                      <div class="font-weight-bold">
                        {{ user.organizationName || user.organization.name }}
                      </div>
                      <div
                        v-if="
                          user.organization &&
                          user.organization.organizationType
                        "
                        class="text-muted text-sm"
                      >
                        {{ user.organization.organizationType }}
                      </div>
                    </div>
                    <div v-else class="text-muted">-</div>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="getRoleBadgeClass(user.roleType)"
                    >
                      {{ getRoleDisplayName(user.roleType) }}
                    </span>
                  </td>
                  <td>{{ user.email }}</td>
                  <td>{{ formatDate(user.createdAt) }}</td>
                  <td>
                    <div class="action-buttons">
                      <button
                        v-permission="'ADMIN_WRITE'"
                        @click="openEditModal(user)"
                        class="btn-sm btn-outline"
                      >
                        수정
                      </button>
                      <button
                        v-permission="'ADMIN_DELETE'"
                        @click="confirmDelete(user)"
                        class="btn-sm btn-danger"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="businessUsers.length === 0">
                  <td colspan="8" class="text-center text-muted">
                    등록된 기업 계정이 없습니다.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 계정 생성/수정 모달 -->
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>
              {{ modalMode === 'create' ? '기업 계정 추가' : '기업 계정 수정' }}
            </h3>
            <button @click="closeModal" class="modal-close">&times;</button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitForm">
              <div class="form-group">
                <label for="organizationId">소속 기업 *</label>
                <select
                  id="organizationId"
                  v-model="form.organizationId"
                  class="form-control"
                  :class="{ 'is-invalid': errors.organizationId }"
                  required
                  :disabled="modalMode === 'edit'"
                >
                  <option value="">기업을 선택하세요</option>
                  <option
                    v-for="org in organizations"
                    :key="org.id"
                    :value="org.id"
                  >
                    {{ org.name }} ({{ org.organizationType }})
                  </option>
                </select>
              </div>

              <div class="form-row">
                <div class="form-group half-width">
                  <label for="accountId">계정 ID *</label>
                  <input
                    id="accountId"
                    v-model="form.accountId"
                    :disabled="modalMode === 'edit'"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.accountId }"
                    required
                    minlength="5"
                    maxlength="20"
                    pattern="^[a-z0-9]+$"
                    title="소문자와 숫자만 사용 가능합니다"
                  />
                </div>
                <div class="form-group half-width">
                  <label for="role">역할 *</label>
                  <select
                    id="role"
                    v-model="form.role"
                    class="form-control"
                    :class="{ 'is-invalid': errors.role }"
                    required
                  >
                    <option value="">역할을 선택하세요</option>
                    <option
                      v-for="option in USER_ROLE_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="form-group">
                <label for="accountPwd"
                  >비밀번호
                  {{ modalMode === 'edit' ? '(변경시에만 입력)' : '*' }}</label
                >
                <input
                  id="accountPwd"
                  v-model="form.accountPwd"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': errors.accountPwd }"
                  :required="modalMode === 'create'"
                  minlength="8"
                  maxlength="16"
                />
              </div>

              <div class="form-row">
                <div class="form-group half-width">
                  <label for="name">이름</label>
                  <input
                    id="name"
                    v-model="form.name"
                    type="text"
                    class="form-control"
                    maxlength="50"
                  />
                </div>
                <div class="form-group half-width">
                  <label for="email">이메일 *</label>
                  <input
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': errors.email }"
                    required
                    maxlength="50"
                  />
                </div>
              </div>

              <div class="form-group">
                <label for="phoneNum">전화번호 *</label>
                <input
                  id="phoneNum"
                  v-model="form.phoneNum"
                  type="tel"
                  class="form-control"
                  :class="{ 'is-invalid': errors.phoneNum }"
                  placeholder="010-1234-5678"
                  required
                />
              </div>

              <div class="form-group">
                <label>주소 *</label>
                <div class="address-inputs">
                  <div class="form-row">
                    <input
                      v-model="form.zoneCode"
                      type="text"
                      class="form-control zone-code"
                      :class="{ 'is-invalid': errors.zoneCode }"
                      placeholder="우편번호"
                      maxlength="15"
                      readonly
                    />
                    <button
                      type="button"
                      @click="handleSearchAddress"
                      class="btn btn-outline address-search-btn"
                    >
                      도로명 주소 검색
                    </button>
                  </div>
                  <input
                    v-model="form.address"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.address }"
                    placeholder="기본주소 (주소 검색 버튼 클릭)"
                    maxlength="255"
                    readonly
                  />
                  <input
                    v-model="form.addressDetail"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.addressDetail }"
                    placeholder="상세주소"
                    maxlength="255"
                  />
                  <input
                    v-model="form.buildingName"
                    type="text"
                    class="form-control"
                    placeholder="건물명"
                    maxlength="255"
                  />
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button @click="closeModal" class="btn btn-secondary">취소</button>
            <button @click="submitForm" class="btn btn-primary">
              {{ modalMode === 'create' ? '추가' : '수정' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { apiClient } from '@/services/auth-header'
import { useToast } from '@/composables/useToast'
import { formatDate, searchAddress } from '@/common/common-function'
import {
  USER_ROLE_TYPES,
  USER_ROLE_DISPLAY_NAMES,
  USER_ROLE_BADGE_CLASSES,
  USER_ROLE_OPTIONS,
} from '@/constants/organization'
import { useModal } from '@/composables/useModal'

const toast = useToast()

// 데이터
const businessUsers = ref([])
const organizations = ref([])

// 모달 관련
const {
  showModal,
  modalMode,
  openCreateModal: baseOpenCreateModal,
  openEditModal: baseOpenEditModal,
  closeModal: baseCloseModal,
} = useModal()
const form = reactive({
  id: null,
  organizationId: null,
  role: '',
  accountId: '',
  accountPwd: '',
  email: '',
  name: '',
  phoneNum: '',
  address: '',
  addressDetail: '',
  buildingName: '',
  zoneCode: '',
})
const errors = ref({})

// 페이지 로드 시 데이터 조회
onMounted(async () => {
  await Promise.all([fetchBusinessUsers(), fetchOrganizations()])
})

// 기업 계정 목록 조회 (BUSINESS, AGENCY 역할만)
const fetchBusinessUsers = async () => {
  try {
    // BUSINESS와 AGENCY 역할의 사용자들을 병렬로 조회
    const [businessResponse, agencyResponse] = await Promise.all([
      apiClient.get(`/users/by-role/${USER_ROLE_TYPES.BUSINESS}`),
      apiClient.get(`/users/by-role/${USER_ROLE_TYPES.AGENCY}`),
    ])
    businessUsers.value = [...businessResponse.data, ...agencyResponse.data]
  } catch (error) {
    toast.error('기업 계정 목록을 불러오는데 실패했습니다.')
  }
}

// 기업 목록 조회
const fetchOrganizations = async () => {
  try {
    const response = await apiClient.get('/organizations')
    organizations.value = response.data
  } catch (error) {
    toast.error('기업 목록을 불러오는데 실패했습니다.')
  }
}

// 생성 모달 열기
const openCreateModal = () => {
  baseOpenCreateModal()
  resetForm()
}

// 수정 모달 열기
const openEditModal = user => {
  baseOpenEditModal()
  errors.value = {}
  form.id = user.id
  form.organizationId = user.organizationId || user.organization?.id || null
  form.role = user.roleType
  form.accountId = user.accountId
  form.accountPwd = ''
  form.email = user.email
  form.name = user.name || ''
  form.phoneNum = user.phoneNum || ''
  form.address = user.address || ''
  form.addressDetail = user.addressDetail || ''
  form.buildingName = user.buildingName || ''
  form.zoneCode = user.zoneCode || ''
}

// 모달 닫기
const closeModal = () => {
  baseCloseModal()
  resetForm()
}

// 폼 초기화
const resetForm = () => {
  form.id = null
  form.organizationId = null
  form.role = ''
  form.accountId = ''
  form.accountPwd = ''
  form.email = ''
  form.name = ''
  form.phoneNum = ''
  form.address = ''
  form.addressDetail = ''
  form.buildingName = ''
  form.zoneCode = ''
  errors.value = {}
}

const validateForm = () => {
  const newErrors = {}
  if (!form.organizationId) newErrors.organizationId = true
  if (!form.accountId) newErrors.accountId = true
  if (!form.role) newErrors.role = true
  if (modalMode.value === 'create' && !form.accountPwd) {
    newErrors.accountPwd = true
  }
  if (!form.email) {
    newErrors.email = true
  } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
    newErrors.email = true
    toast.error('유효한 이메일 주소를 입력해주세요.')
  }

  if (!form.phoneNum) newErrors.phoneNum = true
  if (!form.zoneCode) newErrors.zoneCode = true
  if (!form.address) newErrors.address = true
  if (!form.addressDetail) newErrors.addressDetail = true

  errors.value = newErrors
  return Object.keys(newErrors).length === 0
}

// 폼 제출
const submitForm = async () => {
  if (!validateForm()) {
    toast.error('필수 입력 항목을 확인해주세요.')
    return
  }

  try {
    const formData = {
      organizationId: form.organizationId,
      role: form.role,
      accountId: form.accountId,
      email: form.email,
      name: form.name || null,
      phoneNum: form.phoneNum || null,
      address: form.address || null,
      addressDetail: form.addressDetail || null,
      buildingName: form.buildingName || null,
      zoneCode: form.zoneCode || null,
      birthDate: null, // 현재 폼에 없는 필드이지만 DTO 요구사항
      genderType: null, // 현재 폼에 없는 필드이지만 DTO 요구사항
    }

    if (modalMode.value === 'create') {
      const createData = { ...formData, accountPwd: form.accountPwd }
      await apiClient.post('/users/organization', createData)
      toast.success('기업 계정이 성공적으로 추가되었습니다.')
    } else {
      if (!form.id) {
        toast.error('사용자 ID가 없어 수정할 수 없습니다.')
        return
      }
      const updateData = form.accountPwd.trim()
        ? { ...formData, accountPwd: form.accountPwd }
        : formData
      await apiClient.put(`/users/organization/${form.id}`, updateData)
      toast.success('기업 계정 정보가 성공적으로 수정되었습니다.')
    }

    closeModal()
    await fetchBusinessUsers()
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      (modalMode.value === 'create'
        ? '기업 계정 추가에 실패했습니다.'
        : '기업 계정 수정에 실패했습니다.')
    toast.error(errorMessage)
  }
}

// 삭제 확인
const confirmDelete = user => {
  if (confirm(`'${user.accountId}' 계정을 삭제하시겠습니까?`)) {
    deleteUser(user.id)
  }
}

// 계정 삭제
const deleteUser = async id => {
  try {
    await apiClient.delete(`/users/${id}`)
    toast.success('기업 계정이 성공적으로 삭제되었습니다.')
    await fetchBusinessUsers()
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || '기업 계정 삭제에 실패했습니다.'
    toast.error(errorMessage)
  }
}

// 주소 검색
const handleSearchAddress = async () => {
  try {
    const data = await searchAddress()
    form.address = data.address
    form.zoneCode = data.zonecode
    form.buildingName = data.buildingName || ''
  } catch (error) {
    toast.error('주소 검색에 실패했습니다.')
  }
}

// 유틸리티 함수들
const getRoleDisplayName = roleType => {
  return USER_ROLE_DISPLAY_NAMES[roleType] || roleType
}

const getRoleBadgeClass = roleType => {
  return USER_ROLE_BADGE_CLASSES[roleType] || 'badge-secondary'
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.page-actions {
  display: flex;
  gap: 10px;
}

.action-buttons {
  display: flex;
  gap: 5px;
  align-items: center;
}

.font-weight-bold {
  font-weight: 600;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.badge-primary {
  background-color: #007bff;
  color: white;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #dee2e6;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.form-row {
  display: flex;
  gap: 15px;
  align-items: center;
}

.address-search-btn {
  white-space: nowrap;
  min-width: 140px;
}

.zone-code {
  max-width: 120px;
}

.half-width {
  flex: 1;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control.is-invalid {
  border-color: #dc3545;
}
.form-control.is-invalid:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.address-inputs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.zone-code {
  max-width: 200px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-outline {
  background-color: transparent;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #6c757d;
}

.text-sm {
  font-size: 12px;
}

.mt20 {
  margin-top: 20px;
}

.white-panel {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-wrap {
  overflow-x: auto;
}

.scroll-box {
  min-height: 200px;
}

.table-type1 {
  width: 100%;
  border-collapse: collapse;
}

.table-type1 th,
.table-type1 td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.table-type1 th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.breadcrumb {
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
}

.breadcrumb-item {
  color: #6c757d;
}

.breadcrumb-item:not(:last-child)::after {
  content: ' > ';
  margin: 0 8px;
}

.breadcrumb-item.active {
  color: #495057;
  font-weight: 500;
}

.contents {
  padding: 20px;
}

.layout {
  max-width: 1200px;
}
</style>
